import React from 'react';
import { X } from 'lucide-react';
import LearningStatusBadge from '../../ui/LearningStatusBadge';

export default function QuizHeader({ 
    current, 
    total, 
    onClose, 
    status, 
    theme = 'dark' 
}) {
    const [showAbortConfirm, setShowAbortConfirm] = React.useState(false);
    const isDark = theme === 'dark';

    return (
        <div className={`shrink-0 w-full px-3 md:px-12 py-3 md:py-6 flex items-center justify-between border-b ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-black/5 bg-black/[0.02]'} backdrop-blur-md z-50`}>
            {/* Left: Status Badge & Label */}
            <div className="md:w-1/3 flex items-center gap-3">
                <div className="relative group">
                    <LearningStatusBadge status={status} size="sm" />
                    <div className={`absolute inset-0 ${isDark ? 'bg-white/20' : 'bg-black/10'} rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity`} />
                </div>
                <div className="hidden md:flex flex-col">
                    <span className={`text-[8px] font-black uppercase tracking-[0.2em] ${isDark ? 'text-white/30' : 'text-slate-400'} leading-none mb-1`}>Fortschritt</span>
                    <span className={`text-[11px] font-black ${isDark ? 'text-white/90' : 'text-slate-800'} leading-none italic`}>
                        {status === 'LEARNED' ? 'Meisterhaft' : status === 'INTERMEDIATE' ? 'Gelernt' : 'Am Anfang'}
                    </span>
                </div>
            </div>

            {/* Center: Progress & Counter */}
            <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[8px] font-black uppercase tracking-[0.4em] ${isDark ? 'text-white/40' : 'text-slate-500'} drop-shadow-sm`}>Frage</span>
                    <div className={`flex items-center text-[11px] font-black ${isDark ? 'text-white' : 'text-slate-800'} tabular-nums`}>
                        <span>{current}</span>
                        <span className={`mx-1 ${isDark ? 'text-white/20' : 'text-slate-300'}`}>/</span>
                        <span className={isDark ? 'text-white/40' : 'text-slate-500'}>{total}</span>
                    </div>
                </div>
                <div className={`w-28 md:w-36 h-1.5 ${isDark ? 'bg-white/5 border-white/5' : 'bg-slate-200 border-slate-300'} rounded-full overflow-hidden border p-[1px]`}>
                    <div 
                        className="h-full bg-plantlix-500 rounded-full transition-all duration-700 ease-out shadow-[0_0_12px_rgba(39,145,106,0.35)]"
                        style={{ width: `${(current / total) * 100}%` }}
                    />
                </div>
            </div>

            {/* Right: Close Button */}
            <div className="md:w-1/3 flex justify-end">
                <button 
                    onClick={() => setShowAbortConfirm(true)}
                    className={`group relative flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-2xl ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-slate-200 shadow-sm'} hover:bg-rose-500/20 hover:border-rose-500/30 transition-all duration-300 active:scale-90`}
                    aria-label="Quiz verlassen"
                >
                    <X className={`w-5 h-5 md:w-6 md:h-6 ${isDark ? 'text-white/40' : 'text-slate-400'} group-hover:text-rose-500 group-hover:rotate-90 transition-all duration-300`} strokeWidth={3} />
                </button>
            </div>

            {/* Abort Confirmation Modal (Glassmorphism) */}
            {showAbortConfirm && (
                <div className="fixed inset-0 z-[6000] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className={`
                        w-full max-w-sm p-8 rounded-[2.5rem] border backdrop-blur-2xl shadow-2xl space-y-8 animate-in zoom-in-95 duration-300
                        ${isDark ? 'bg-slate-900/80 border-white/10 text-white' : 'bg-white/90 border-slate-200 text-slate-900'}
                    `}>
                        <div className="text-center space-y-2">
                            <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-500/30">
                                <X className="w-8 h-8 text-rose-500" strokeWidth={3} />
                            </div>
                            <h3 className="text-2xl font-black italic tracking-tight">Quiz abbrechen?</h3>
                            <p className={`text-sm font-medium ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                                Dein aktueller Fortschritt in dieser Runde geht verloren.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button 
                                onClick={onClose}
                                className="w-full py-4 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-black text-lg shadow-xl shadow-rose-500/20 transition-all active:scale-95"
                            >
                                JA, ABBRECHEN
                            </button>
                            <button 
                                onClick={() => setShowAbortConfirm(false)}
                                className={`w-full py-4 rounded-2xl font-black text-lg transition-all active:scale-95 ${isDark ? 'bg-white/5 hover:bg-white/10 border-white/10' : 'bg-slate-100 hover:bg-slate-200 border-slate-200'} border`}
                            >
                                WEITERMACHEN
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
